import React from 'react';
import { Check, X, Star, Zap, Crown } from 'lucide-react';
import { Link } from 'react-router-dom';
import './Pricing.scss';

interface PricingTier {
  id: string;
  name: string;
  description: string;
  price: string;
  period: string;
  accountCap: string;
  feePercentage: string;
  icon: React.ElementType;
  popular?: boolean;
  features: string[];
  limitations: string[];
  ctaText: string;
  ctaVariant: 'primary' | 'secondary' | 'outline';
}

const Pricing: React.FC = () => {
  const generateSignupUrl = (planId: string) => {
    const params = new URLSearchParams({
      plan: planId,
      return: '/dashboard'
    });
    return `/signup?${params.toString()}`;
  };

  const pricingTiers: PricingTier[] = [
    {
      id: 'free',
      name: 'Starter',
      description: 'Perfect for beginners exploring automated trading',
      price: 'Free',
      period: 'forever',
      accountCap: '$5,000',
      feePercentage: '5%',
      icon: Zap,
      features: [
        'Basic AI trading algorithms',
        'Real-time market data',
        'Email support',
        'Mobile app access',
        'Basic portfolio analytics',
        'Standard execution speed'
      ],
      limitations: [
        'Limited to 3 trading pairs',
        'Basic risk management',
        'Community support only'
      ],
      ctaText: 'Get Started Free',
      ctaVariant: 'secondary'
    },
    {
      id: 'pro',
      name: 'Professional',
      description: 'Advanced features for serious traders',
      price: '$25',
      period: 'per month',
      accountCap: '$50,000',
      feePercentage: '1%',
      icon: Star,
      popular: true,
      features: [
        'Advanced AI trading strategies',
        'Real-time market analysis',
        'Priority email & chat support',
        'Advanced portfolio analytics',
        'Risk management tools',
        'Faster execution speeds',
        'Custom trading parameters',
        'Historical backtesting',
        'Multiple exchange support',
        'API access'
      ],
      limitations: [
        'Limited to 20 trading pairs',
        'Standard risk limits'
      ],
      ctaText: 'Start Free Trial',
      ctaVariant: 'primary'
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      description: 'Maximum performance for institutional traders',
      price: '$120',
      period: 'per month',
      accountCap: '$1,000,000',
      feePercentage: '0%',
      icon: Crown,
      features: [
        'Institutional-grade AI algorithms',
        'Ultra-low latency execution',
        'Dedicated account manager',
        'Advanced risk management suite',
        'Custom strategy development',
        'Unlimited trading pairs',
        'White-glove onboarding',
        'Advanced analytics & reporting',
        'Priority server allocation',
        'Custom API endpoints',
        'Regulatory compliance tools',
        '24/7 phone support'
      ],
      limitations: [],
      ctaText: 'Contact Sales',
      ctaVariant: 'secondary'
    }
  ];

  const faqs = [
    {
      question: 'How does the fee structure work?',
      answer: 'Our fees are calculated only on profitable trades. For the Free tier, we take 5% of earnings. Pro tier is 1% of earnings, and Enterprise has no fees on earnings.'
    },
    {
      question: 'What happens if I exceed my account cap?',
      answer: 'If your account approaches the cap, you\'ll receive notifications to upgrade. Trading will be temporarily paused until you upgrade or reduce your account balance.'
    },
    {
      question: 'Can I switch plans anytime?',
      answer: 'Yes! You can upgrade or downgrade your plan at any time. Upgrades take effect immediately, while downgrades take effect at your next billing cycle.'
    },
    {
      question: 'Is there a free trial for paid plans?',
      answer: 'Yes, we offer a 14-day free trial for both Pro and Enterprise plans. No credit card required to start.'
    },
    {
      question: 'What exchanges do you support?',
      answer: 'We support 50+ major exchanges including Binance, Coinbase Pro, Kraken, UniSwap, and many more. Enterprise users get priority for new exchange integrations.'
    },
    {
      question: 'How secure is my account information?',
      answer: 'We use bank-grade security with 256-bit encryption, cold storage for funds, and never store your exchange passwords or private keys.'
    }
  ];

  return (
    <div className="pricing">
      {/* Hero Section */}
      <section className="pricing__hero section">
        <div className="container">
          <div className="pricing__hero-content">
            <h1 className="pricing__title">
              Choose Your <span className="gradient-text">Trading Plan</span>
            </h1>
            <p className="pricing__subtitle">
              Start with our free tier and scale up as your portfolio grows. 
              All plans include our industry-leading AI trading technology.
            </p>
            
            <div className="pricing__guarantee">
              <div className="pricing__guarantee-badge">
                <Check size={16} />
                <span>14-day money-back guarantee</span>
              </div>
              <div className="pricing__guarantee-badge">
                <Check size={16} />
                <span>No setup fees</span>
              </div>
              <div className="pricing__guarantee-badge">
                <Check size={16} />
                <span>Cancel anytime</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pricing__plans section">
        <div className="container">
          <div className="pricing__grid">
            {pricingTiers.map((tier) => (
              <div
                key={tier.id}
                className={`pricing__card ${tier.popular ? 'pricing__card--popular' : ''}`}
              >
                {tier.popular && (
                  <div className="pricing__popular-badge">
                    <Star size={16} />
                    <span>Most Popular</span>
                  </div>
                )}
                
                <div className="pricing__card-header">
                  <div className="pricing__icon">
                    <tier.icon size={24} />
                  </div>
                  <h3 className="pricing__plan-name">{tier.name}</h3>
                  <p className="pricing__plan-description">{tier.description}</p>
                </div>

                <div className="pricing__card-pricing">
                  <div className="pricing__price">
                    <span className="pricing__price-amount">{tier.price}</span>
                    <span className="pricing__price-period">/{tier.period}</span>
                  </div>
                  
                  <div className="pricing__caps">
                    <div className="pricing__cap-item">
                      <span className="pricing__cap-label">Account Cap:</span>
                      <span className="pricing__cap-value">{tier.accountCap}</span>
                    </div>
                    <div className="pricing__cap-item">
                      <span className="pricing__cap-label">Earnings Fee:</span>
                      <span className="pricing__cap-value">{tier.feePercentage}</span>
                    </div>
                  </div>
                </div>

                <div className="pricing__card-content">
                  <div className="pricing__features">
                    <h4 className="pricing__features-title">Everything included:</h4>
                    <ul className="pricing__features-list">
                      {tier.features.map((feature, index) => (
                        <li key={index} className="pricing__feature">
                          <Check size={16} className="pricing__feature-icon" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    {tier.limitations.length > 0 && (
                      <div className="pricing__limitations">
                        <h4 className="pricing__limitations-title">Limitations:</h4>
                        <ul className="pricing__limitations-list">
                          {tier.limitations.map((limitation, index) => (
                            <li key={index} className="pricing__limitation">
                              <X size={16} className="pricing__limitation-icon" />
                              <span>{limitation}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                <div className="pricing__card-footer">
                  <Link
                    to={
                      tier.id === 'enterprise' 
                        ? '/contact' 
                        : generateSignupUrl(tier.id)
                    }
                    className={`btn btn-${tier.ctaVariant} btn-lg pricing__cta`}
                  >
                    {tier.ctaText}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="pricing__comparison section bg-gray-50">
        <div className="container">
          <div className="pricing__comparison-header">
            <h2 className="pricing__comparison-title">Feature Comparison</h2>
            <p className="pricing__comparison-subtitle">
              See exactly what's included in each plan
            </p>
          </div>

          <div className="pricing__comparison-table">
            <div className="pricing__table-responsive">
              <table className="pricing__table">
                <thead>
                  <tr>
                    <th>Features</th>
                    <th>Starter</th>
                    <th>Professional</th>
                    <th>Enterprise</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Account Cap</td>
                    <td>$5,000</td>
                    <td>$50,000</td>
                    <td>$1,000,000</td>
                  </tr>
                  <tr>
                    <td>Earnings Fee</td>
                    <td>5%</td>
                    <td>1%</td>
                    <td>0%</td>
                  </tr>
                  <tr>
                    <td>Trading Pairs</td>
                    <td>3</td>
                    <td>20</td>
                    <td>Unlimited</td>
                  </tr>
                  <tr>
                    <td>AI Trading Strategies</td>
                    <td>Basic</td>
                    <td>Advanced</td>
                    <td>Institutional</td>
                  </tr>
                  <tr>
                    <td>Execution Speed</td>
                    <td>Standard</td>
                    <td>Fast</td>
                    <td>Ultra-low latency</td>
                  </tr>
                  <tr>
                    <td>Support</td>
                    <td>Email</td>
                    <td>Email + Chat</td>
                    <td>24/7 Phone + Dedicated Manager</td>
                  </tr>
                  <tr>
                    <td>API Access</td>
                    <td><X size={16} /></td>
                    <td><Check size={16} /></td>
                    <td><Check size={16} /> + Custom endpoints</td>
                  </tr>
                  <tr>
                    <td>Custom Strategies</td>
                    <td><X size={16} /></td>
                    <td>Limited</td>
                    <td><Check size={16} /> + Development support</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="pricing__faq section">
        <div className="container">
          <div className="pricing__faq-header">
            <h2 className="pricing__faq-title">Frequently Asked Questions</h2>
            <p className="pricing__faq-subtitle">
              Got questions? We've got answers.
            </p>
          </div>

          <div className="pricing__faq-grid">
            {faqs.map((faq, index) => (
              <div key={index} className="pricing__faq-item">
                <h3 className="pricing__faq-question">{faq.question}</h3>
                <p className="pricing__faq-answer">{faq.answer}</p>
              </div>
            ))}
          </div>

          <div className="pricing__faq-contact">
            <p>Still have questions?</p>
            <Link to="/contact" className="btn btn-primary">
              Contact our sales team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
